import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@datapay-by-datacom/design-system";
import { useWorkPatternImportInvocation } from "../../hooks/WorkPatternImport/useWorkPatternImportInvocation";
import type { WorkPatternImportManagerReturn } from "../../hooks/WorkPatternImport/useWorkPatternImportInvocation";
import { useWorkPatternImportWorkloadsManager } from "../../hooks/WorkPatternImport/useWorkPatternImportWorkloadsManager";
import { useWorkPatternImportNavigationGuard } from "../../hooks/WorkPatternImport/useWorkPatternImportNavigationGuard";
import { useWorkPatternImportCsvParser } from "../../hooks/WorkPatternImport/useWorkPatternImportCsvParser";
import { CompanyContextStep } from "../../../onboarding";
import type { CompanyContext } from "../../../onboarding";
import { ImportProgress } from "../../../shared";
import { WorkPatternImportDropzone } from "./WorkPatternImportDropzone";
import { WorkPatternImportErrors } from "./WorkPatternImportErrors";
import { WorkPatternImportWarnings } from "./WorkPatternImportWarnings";
import { WorkPatternImportTable } from "./WorkPatternImportTable";
import { WorkPatternImportReport } from "./WorkPatternImportReport";
import { WorkPatternCycleGrid } from "./WorkPatternCycleGrid";
import { WorkPatternImportEditModal } from "./WorkPatternImportEditModal";
import { WorkPatternImportLeaveConfirmModal } from "./WorkPatternImportLeaveConfirmModal";
import { WorkPatternImportCancelConfirmModal } from "./WorkPatternImportCancelConfirmModal";
import { WorkPatternImportCommitConfirmModal } from "./WorkPatternImportCommitConfirmModal";
import { WorkPatternImportReplaceFileConfirmModal } from "./WorkPatternImportReplaceFileConfirmModal";
import type { MyPayEnvironment } from "../../../generated/core-api";
import type { ScopeTypeOption } from "../../types";
import { FeatureFlag, useFeatureFlagValue } from "../../../feature-flags";
import {
  clusterRows,
  countTabs,
  formatBlockedImportBanner,
  CLUSTER_CONFIGS,
} from "./workPatternImportClustering";
import { downloadWorkPatternErrorsCsv } from "./workPatternImportErrorsCsv";
import styles from "./WorkPatternImportInput.module.css";

type CommitConfirmMode = "standard" | "overrideWarnings" | "partial" | "partialOverride";

// TODO(#843): Employee-scoped work patterns are being removed. This is hardcoded
// to "Employee Group" for now; the full deletion of the Employee path — the
// ScopeTypeOption type, the scopeType plumbing in the dropzone and invocation hook,
// and the /onboarding/workpatterns/employees route + employee wire DTO in
// Pacific.Agentic.Api (MyPayWorkPatternAdapter/validator/models) — is tracked by
// https://github.com/Datapay-By-Datacom/Pacific.Agentic.Api/issues/843
const SCOPE_TYPE: ScopeTypeOption = "Employee Group";

interface WorkPatternImportInputProps {
  /** Enables the cross-workflow UX convergence layer. */
  redesignEnabled?: boolean;
}

/**
 * Top-level composition for the work pattern import flow.
 *
 * Flow:
 *   1. User enters company code → validated against the Christmas API via
 *      `CompanyContextStep` (shared onboarding component).
 *   2. Once company code is valid, CSV dropzone is enabled.
 *   3. Drop CSV → client parses rows via useWorkPatternImportCsvParser.
 *   4. On successful parse, auto-validate (POST ?validate_only=true) immediately.
 *   5. Show an enabled "Confirm import" button after validation.
 *   6. On confirm: block with an error if issues remain; otherwise confirm (with
 *      an override prompt when warnings are not dismissed) → commit.
 *
 * The hook reads patterns from an internal ref so validate() can be called
 * immediately after setPendingData() in the same async handler without a
 * React state-batching race condition.
 *
 * Step 9 (LADR-039 / #440): `FeatureFlag.EnableWorkloadsCutover` switches which
 * manager hook backs this screen — Track 1 (`useWorkPatternImportInvocation`,
 * invokeAgent workflows) when off, Track 2 (`useWorkPatternImportWorkloadsManager`,
 * Slice 5 workloads REST APIs) when on. Both hooks implement the identical
 * `WorkPatternImportManagerReturn` shape, so the shared body below needs no
 * branching of its own.
 *
 * The switch can't be a plain `flag ? useHookA() : useHookB()` inside one
 * component — that conditionally calls different hooks based on a runtime
 * value, which violates the Rules of Hooks (and would fire both managers'
 * queries/mutations if both were called unconditionally). Instead, this
 * top-level component only ever calls `useFeatureFlagValue` and renders one
 * of two child components — each of which unconditionally calls exactly one
 * manager hook and forwards the result to the shared presentational body.
 * Only the active track's component instance mounts, so only its hook (and
 * therefore its API calls) ever fires.
 */
export function WorkPatternImportInput({
  redesignEnabled = true,
}: WorkPatternImportInputProps = {}) {
  const useWorkloadsCutover = useFeatureFlagValue(FeatureFlag.EnableWorkloadsCutover);

  return useWorkloadsCutover ? (
    <WorkPatternImportInputWorkloads redesignEnabled={redesignEnabled} />
  ) : (
    <WorkPatternImportInputTrackOne redesignEnabled={redesignEnabled} />
  );
}

/** Track 1: invokeAgent Temporal workflows (flag off / default). */
function WorkPatternImportInputTrackOne({
  redesignEnabled,
}: Required<WorkPatternImportInputProps>) {
  const manager = useWorkPatternImportInvocation();
  return (
    <WorkPatternImportInputBody
      manager={manager}
      redesignEnabled={redesignEnabled}
    />
  );
}

/** Track 2: Slice 5 workloads + entity REST APIs (flag on). */
function WorkPatternImportInputWorkloads({
  redesignEnabled,
}: Required<WorkPatternImportInputProps>) {
  const manager = useWorkPatternImportWorkloadsManager();
  return (
    <WorkPatternImportInputBody
      manager={manager}
      redesignEnabled={redesignEnabled}
    />
  );
}

/**
 * Shared presentational + orchestration body. Receives whichever manager
 * (Track 1 or Track 2) is active from the wrapper components above; it has
 * no knowledge of which track backs it.
 */
function WorkPatternImportInputBody({
  manager,
  redesignEnabled,
}: {
  manager: WorkPatternImportManagerReturn;
  redesignEnabled: boolean;
}) {
  // Feature flag: switch from the flat table to the new clustered report (#426).
  // Flag default is OFF — old table stays visible until the flag is turned on.
  const useClusteredReport = useFeatureFlagValue(FeatureFlag.EnableWorkPatternClusteredReport);
  // Feature flag: Edit in UI on blocking-error rows plus the new edit-modal
  // fields (#435). Flag default is OFF — today's behaviour is unchanged.
  const enableErrorEditInUi = useFeatureFlagValue(FeatureFlag.EnableWorkPatternErrorEditInUi);

  const {
    pendingPatterns,
    parseErrors,
    parseWarnings,
    warningsByPatternIndex,
    parseFailedRows,
    patternCsvRowNumbers,
    validationReport,
    commitResult,
    managerError,
    isValidating,
    isCommitting,
    hasData,
    hasParseErrors,
    validationEndedWithoutReport,
    hasUncommittedImportProgress,
    mypayEnvironment,
    rawCsvText,
    setPendingData,
    validate,
    commit,
    clear,
    removePattern,
    setMyPayEnvironment,
    validationStale,
    ignoredWarningIndices,
    hasUnacknowledgedWarnings,
    updatePattern,
    ignoreWarning,
  } = manager;

  const { isBlocked, stay, leaveAnyway } =
    useWorkPatternImportNavigationGuard(hasUncommittedImportProgress);

  const { parseFile, isProcessing } = useWorkPatternImportCsvParser();
  const isApiProcessing = isValidating || isCommitting;
  const parseRequestRef = useRef(0);
  const contextGenerationRef = useRef(0);

  // Local state derived from context step callbacks.
  const [contextBound, setContextBound] = useState(false);
  const [validatedCode, setValidatedCode] = useState<string | null>(null);
  /** Uploaded file retained for compact status and issue-export naming. */
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [pendingReplacementFile, setPendingReplacementFile] =
    useState<File | null>(null);
  const [replacementError, setReplacementError] = useState<string | null>(
    null
  );

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [commitConfirmOpen, setCommitConfirmOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [commitConfirmMode, setCommitConfirmMode] =
    useState<CommitConfirmMode>("standard");
  /** Counts snapshotted when the confirm modal opens — imported, skipped, and kept-warning row counts. */
  const [commitConfirmCounts, setCommitConfirmCounts] = useState<{
    importableCount: number;
    skippedCount: number;
    warningCount: number;
  }>({ importableCount: 0, skippedCount: 0, warningCount: 0 });
  /** Set only when the user clicks Confirm import while blocking errors remain. */
  const [confirmImportFieldError, setConfirmImportFieldError] = useState<
    string | null
  >(null);
  const isInteractionBusy =
    isProcessing || isApiProcessing || pendingReplacementFile !== null;

  useEffect(() => {
    if (isBlocked) {
      setCommitConfirmOpen(false);
      setCancelConfirmOpen(false);
      setPendingReplacementFile(null);
    }
  }, [isBlocked]);

  const handleRequestEdit = useCallback(
    (index: number) => {
      if (isInteractionBusy || isBlocked) {
        return;
      }
      setCommitConfirmOpen(false);
      setCancelConfirmOpen(false);
      setPendingReplacementFile(null);
      setEditingIndex(index);
    },
    [isInteractionBusy, isBlocked]
  );

  const handleEditSave = async (
    index: number,
    updated: Parameters<typeof updatePattern>[1]
  ) => {
    setConfirmImportFieldError(null);
    updatePattern(index, updated);
    await validate();
  };

  const activeResult = commitResult ?? validationReport;

  const visibleParseWarnings = parseWarnings.filter(
    (warning) =>
      warning.patternIndex === undefined ||
      !ignoredWarningIndices.has(warning.patternIndex)
  );
  const showParseWarnings =
    visibleParseWarnings.length > 0 &&
    !hasParseErrors &&
    !activeResult &&
    !isProcessing &&
    !isValidating &&
    !isCommitting;

  const showParseErrorBanner =
    hasParseErrors &&
    (pendingPatterns.length === 0 || validationEndedWithoutReport);

  /** Called when the shared step binds a valid company + env context. */
  const handleContextBound = useCallback(
    (context: CompanyContext) => {
      contextGenerationRef.current += 1;
      setContextBound(true);
      setValidatedCode(context.companyCode);
      setMyPayEnvironment(context.mypayEnvironment as MyPayEnvironment);
    },
    [setMyPayEnvironment]
  );

  /**
   * Called when the bind is dropped (environment change, code edit).
   * Clears the imported batch so a stale commit-ready report can't survive
   * against a new env or code — preserves the previous inline-handler behaviour.
   */
  const handleContextCleared = useCallback(() => {
    contextGenerationRef.current += 1;
    setContextBound(false);
    setValidatedCode(null);
    setConfirmImportFieldError(null);
    setUploadedFile(null);
    setPendingReplacementFile(null);
    setReplacementError(null);
    clear();
  }, [clear]);

  /**
   * Called when the environment selector changes (before bind). Keeps the
   * invocation hook's environment in sync so any in-flight request knows the
   * current env — and clears the imported batch via `clear()`.
   */
  const handleEnvironmentChange = useCallback(
    (env: MyPayEnvironment | null) => {
      if (env !== mypayEnvironment) {
        contextGenerationRef.current += 1;
        setConfirmImportFieldError(null);
        setUploadedFile(null);
        setPendingReplacementFile(null);
        setReplacementError(null);
        clear();
      }
      setMyPayEnvironment(env);
    },
    [mypayEnvironment, clear, setMyPayEnvironment]
  );

  /**
   * On file drop: parse CSV (WorkPatternCode column optional — Employee Group only),
   * then immediately trigger the validate-only pre-flight.
   * The hook's ref ensures validate() sees the new patterns even before React
   * has committed the state update from setPendingData().
   * The validated company code comes from the context bind, not the CSV.
   */
  const processSelectedFile = async (
    file: File,
    preserveCurrentOnFailure = false
  ): Promise<void> => {
    const parseRequest = ++parseRequestRef.current;
    const contextGeneration = contextGenerationRef.current;
    setReplacementError(null);
    const result = await parseFile(file);
    if (
      parseRequest !== parseRequestRef.current ||
      contextGeneration !== contextGenerationRef.current
    ) {
      return;
    }
    const validPatterns = result.data ?? [];
    const hasValidRows = validPatterns.length > 0;

    if (hasValidRows) {
      setConfirmImportFieldError(null);
      setUploadedFile(file);
      // Track 2 needs the raw File (9th arg) for multipart createOnboardingImport.
      // Track 1 stores rawCsvText (8th) for the errors-CSV download; Track 2
      // stores it too so the same UI affordance works behind the cutover flag.
      setPendingData(
        validPatterns,
        result.errors ?? [],
        result.warnings ?? [],
        validatedCode,
        SCOPE_TYPE,
        result.failedRows ?? [],
        result.patternCsvRowNumbers ?? [],
        result.rawText ?? null,
        file,
        result.dayColumnCountFromHeader
      );
      await validate();
    } else if (preserveCurrentOnFailure) {
      setReplacementError(
        `${file.name} did not contain any usable work patterns. The current file and validation results were kept.`
      );
    } else {
      setUploadedFile(file);
      setPendingData(
        [],
        result.errors ?? ["Unknown parsing error"],
        result.warnings ?? [],
        undefined,
        undefined,
        result.failedRows ?? [],
        result.patternCsvRowNumbers ?? [],
        result.rawText ?? null,
        undefined, // no file — nothing usable was parsed to import
        result.dayColumnCountFromHeader
      );
    }
  };

  const handleFileSelected = async (file: File): Promise<void> => {
    if (isInteractionBusy || isBlocked) {
      return;
    }
    if (redesignEnabled && hasUncommittedImportProgress) {
      setEditingIndex(null);
      setCommitConfirmOpen(false);
      setCancelConfirmOpen(false);
      setPendingReplacementFile(file);
      return;
    }
    await processSelectedFile(file);
  };

  const handleConfirmFileReplacement = async (): Promise<void> => {
    const file = pendingReplacementFile;
    setPendingReplacementFile(null);
    if (file) {
      await processSelectedFile(file, true);
    }
  };

  const activeResultPhase: "validation" | "commit" | undefined = commitResult
    ? "commit"
    : validationReport
      ? "validation"
      : undefined;

  const validationTabCounts = useMemo(() => {
    if (!validationReport || commitResult) {
      return null;
    }
    const rows = clusterRows({
      patterns: pendingPatterns,
      result: validationReport,
      warnings: warningsByPatternIndex,
      ignoredWarningIndices,
      parseErrors,
      parseFailedRows,
      patternCsvRowNumbers,
    });
    return countTabs(rows);
  }, [
    validationReport,
    commitResult,
    pendingPatterns,
    warningsByPatternIndex,
    ignoredWarningIndices,
    parseErrors,
    parseFailedRows,
    patternCsvRowNumbers,
  ]);

  /**
   * Map of csvRowNumber → reason messages for all blocking-error and warning
   * rows in the active result. Both kinds are included in the downloadable
   * issues CSV so consultants can fix or review them before re-uploading.
   * Passed rows are always excluded.
   *
   * When there is no active result yet (all rows failed client-side parsing,
   * so no valid patterns were passed to validate()), the map is built directly
   * from parseFailedRows so the Download errors CSV button can still appear.
   */
  const errorReasonsByCsvRow = useMemo(() => {
    if (!activeResult) {
      // No validation/commit result yet — fall back to parse-failed rows so
      // a CSV where every row is unparseable still offers the download button.
      if (parseFailedRows.length === 0) return new Map<number, string[]>();
      const map = new Map<number, string[]>();
      for (const row of parseFailedRows) {
        map.set(row.csvRowNumber, [row.error]);
      }
      return map;
    }
    const rows = clusterRows({
      patterns: pendingPatterns,
      result: activeResult,
      warnings: warningsByPatternIndex,
      ignoredWarningIndices,
      parseErrors,
      parseFailedRows,
      patternCsvRowNumbers,
    });
    const map = new Map<number, string[]>();
    for (const row of rows) {
      const kind = CLUSTER_CONFIGS[row.clusterId].kind;
      if (kind === "error" && row.errorMessage) {
        map.set(row.csvRowNumber, [row.errorMessage]);
      } else if (kind === "warning" && row.warningMessages && row.warningMessages.length > 0) {
        map.set(row.csvRowNumber, row.warningMessages);
      }
    }
    return map;
  }, [
    activeResult,
    pendingPatterns,
    warningsByPatternIndex,
    ignoredWarningIndices,
    parseErrors,
    parseFailedRows,
    patternCsvRowNumbers,
  ]);

  const handleDownloadErrors = useCallback(() => {
    if (!rawCsvText || errorReasonsByCsvRow.size === 0) return;
    const filename = uploadedFile
      ? `${uploadedFile.name.replace(/\.csv$/i, "")}-issues.csv`
      : "work-pattern-import-issues.csv";
    downloadWorkPatternErrorsCsv(
      rawCsvText,
      errorReasonsByCsvRow,
      filename,
      pendingPatterns,
      patternCsvRowNumbers
    );
  }, [
    rawCsvText,
    errorReasonsByCsvRow,
    uploadedFile,
    pendingPatterns,
    patternCsvRowNumbers,
  ]);

  const canDownloadErrors =
    !!rawCsvText && errorReasonsByCsvRow.size > 0;

  const performClearAll = useCallback(() => {
    setEditingIndex(null);
    setCommitConfirmOpen(false);
    setCancelConfirmOpen(false);
    setConfirmImportFieldError(null);
    setUploadedFile(null);
    setPendingReplacementFile(null);
    setReplacementError(null);
    clear();
  }, [clear]);

  const handleRequestCancelImport = useCallback(() => {
    if (isInteractionBusy || isBlocked) {
      return;
    }
    setEditingIndex(null);
    setCommitConfirmOpen(false);
    setPendingReplacementFile(null);
    if (hasUncommittedImportProgress) {
      setCancelConfirmOpen(true);
      return;
    }
    setCancelConfirmOpen(false);
    performClearAll();
  }, [
    hasUncommittedImportProgress,
    isInteractionBusy,
    isBlocked,
    performClearAll,
  ]);

  const handleConfirmCancelImport = useCallback(() => {
    setCancelConfirmOpen(false);
    performClearAll();
  }, [performClearAll]);

  const handleValidate = useCallback(async () => {
    if (isInteractionBusy || isBlocked) {
      return;
    }
    setConfirmImportFieldError(null);
    await validate();
  }, [isInteractionBusy, isBlocked, validate]);

  const handleRequestCommitConfirm = useCallback(() => {
    if (isInteractionBusy || isBlocked) {
      return;
    }

    setEditingIndex(null);
    setCancelConfirmOpen(false);
    setCommitConfirmOpen(false);
    setPendingReplacementFile(null);

    if (!validationTabCounts) {
      return;
    }

    const { errors, passed, warnings: warningRows } = validationTabCounts;
    const eligibleCount = passed + warningRows;

    if (errors > 0 && eligibleCount === 0) {
      // Every row has a blocking error — keep the footer banner, no modal.
      setConfirmImportFieldError(
        formatBlockedImportBanner(
          errors,
          validationTabCounts.all,
          eligibleCount
        )
      );
      return;
    }

    setConfirmImportFieldError(null);

    if (errors > 0 && eligibleCount > 0) {
      if (!redesignEnabled && !useClusteredReport) {
        setConfirmImportFieldError(
          formatBlockedImportBanner(
            errors,
            validationTabCounts.all,
            passed
          )
        );
        return;
      }
      setCommitConfirmCounts({
        importableCount: eligibleCount,
        skippedCount: errors,
        warningCount: warningRows,
      });
      setCommitConfirmMode(warningRows > 0 ? "partialOverride" : "partial");
      setCommitConfirmOpen(true);
      return;
    }

    // No blocking errors — full batch commit.
    setCommitConfirmCounts({
      importableCount: eligibleCount,
      skippedCount: 0,
      warningCount: warningRows,
    });
    if (hasUnacknowledgedWarnings) {
      setCommitConfirmMode("overrideWarnings");
      setCommitConfirmOpen(true);
      return;
    }
    setCommitConfirmMode("standard");
    setCommitConfirmOpen(true);
  }, [
    validationTabCounts,
    hasUnacknowledgedWarnings,
    isInteractionBusy,
    isBlocked,
    redesignEnabled,
    useClusteredReport,
  ]);

  const failedIndices = new Set(
    (activeResult?.errors ?? []).map((e) => e.index)
  );
  const succeededPatterns = activeResult
    ? pendingPatterns.filter((_, index) => !failedIndices.has(index))
    : [];

  const longestCycleDays = pendingPatterns.reduce(
    (max, pattern) => Math.max(max, pattern.cycleDays),
    0
  );
  const hideDropzone =
    !redesignEnabled &&
    pendingPatterns.length > 0 &&
    (isValidating ||
      isCommitting ||
      validationReport !== null ||
      commitResult !== null);

  return (
    <div className={styles.inputContainer}>
      <CompanyContextStep
        disabled={isApiProcessing}
        onContextBound={handleContextBound}
        onContextCleared={handleContextCleared}
        onEnvironmentChange={handleEnvironmentChange}
      />

      {!hideDropzone && (
        <WorkPatternImportDropzone
          onFileSelected={handleFileSelected}
          isProcessing={
            redesignEnabled ? isInteractionBusy : isProcessing || isApiProcessing
          }
          selectedFile={redesignEnabled ? uploadedFile : undefined}
          isDisabled={!contextBound}
          scopeType={SCOPE_TYPE}
        />
      )}
      {redesignEnabled && uploadedFile && pendingPatterns.length > 0 && (
        <p className={styles.fileStatus} role="status">
          {pendingPatterns.length} work pattern
          {pendingPatterns.length === 1 ? "" : "s"} parsed
        </p>
      )}
      {redesignEnabled && replacementError && (
        <WorkPatternImportErrors
          errors={[replacementError]}
          title="File replacement failed:"
        />
      )}

      {showParseErrorBanner && (
        <WorkPatternImportErrors
          errors={parseErrors}
          title="CSV Parsing Errors:"
        />
      )}

      {/* Download button for fully-unparseable uploads: every row failed client
          parsing so there are no pendingPatterns and no activeResult — the
          report section below never renders in this state (#414). */}
      {showParseErrorBanner && !activeResult && pendingPatterns.length === 0 && canDownloadErrors && (
        <Button
          variant="secondary"
          onPress={handleDownloadErrors}
          isDisabled={isProcessing}
        >
          Download issues CSV
        </Button>
      )}

      {showParseWarnings && (
        <WorkPatternImportWarnings
          warnings={visibleParseWarnings}
          onCancelImport={handleRequestCancelImport}
        />
      )}

      {managerError && (
        <WorkPatternImportErrors errors={[managerError]} title="Error:" />
      )}

      {(isValidating || isCommitting) && (
        <ImportProgress
          heading={
            redesignEnabled
              ? isValidating
                ? "Validating patterns…"
                : "Importing patterns…"
              : "Importing patterns…"
          }
          progressbarLabel={
            redesignEnabled && isValidating
              ? "Validation in progress"
              : "Import in progress"
          }
          detail={
            redesignEnabled
              ? `${isValidating ? "Validating" : "Importing"} ${
                  isCommitting && commitConfirmCounts.importableCount > 0
                    ? commitConfirmCounts.importableCount
                    : pendingPatterns.length
                } pattern${
                  (isCommitting && commitConfirmCounts.importableCount > 0
                    ? commitConfirmCounts.importableCount
                    : pendingPatterns.length) === 1
                    ? ""
                    : "s"
                } · longest cycle ${longestCycleDays} days…`
              : `Committing ${pendingPatterns.length} pattern${
                  pendingPatterns.length === 1 ? "" : "s"
                } · longest cycle ${longestCycleDays} days…`
          }
        />
      )}

      {hasData && activeResult && (
        useClusteredReport ? (
          <WorkPatternImportReport
            patterns={pendingPatterns}
            result={activeResult}
            resultPhase={activeResultPhase}
            patternCsvRowNumbers={patternCsvRowNumbers}
            parseErrors={parseErrors}
            warnings={warningsByPatternIndex}
            parseFailedRows={parseFailedRows}
            ignoredWarningIndices={ignoredWarningIndices}
            validationStale={validationStale}
            onClearAll={handleRequestCancelImport}
            onValidate={handleValidate}
            onRequestCommitConfirm={handleRequestCommitConfirm}
            commitBlockedMessage={confirmImportFieldError}
            onEditPattern={handleRequestEdit}
            onIgnoreWarning={ignoreWarning}
            isValidating={isValidating}
            isCommitting={isCommitting}
            isProcessing={isProcessing || pendingReplacementFile !== null}
            canDownloadErrors={canDownloadErrors}
            onDownloadErrors={handleDownloadErrors}
            enableErrorEditInUi={enableErrorEditInUi}
            redesignEnabled={redesignEnabled}
          />
        ) : (
          <WorkPatternImportTable
            patterns={pendingPatterns}
            result={activeResult}
            resultPhase={activeResultPhase}
            patternCsvRowNumbers={patternCsvRowNumbers}
            parseErrors={parseErrors}
            warnings={warningsByPatternIndex}
            parseFailedRows={parseFailedRows}
            ignoredWarningIndices={ignoredWarningIndices}
            validationStale={validationStale}
            onRemovePattern={removePattern}
            onClearAll={handleRequestCancelImport}
            onValidate={handleValidate}
            onRequestCommitConfirm={handleRequestCommitConfirm}
            commitBlockedMessage={confirmImportFieldError}
            onEditPattern={handleRequestEdit}
            onIgnoreWarning={ignoreWarning}
            isProcessing={isInteractionBusy}
            isCommitting={isCommitting}
            canDownloadErrors={canDownloadErrors}
            onDownloadErrors={handleDownloadErrors}
            enableErrorEditInUi={enableErrorEditInUi}
            redesignEnabled={redesignEnabled}
          />
        )
      )}

      <WorkPatternImportEditModal
        isOpen={!isBlocked && editingIndex !== null}
        pattern={
          editingIndex !== null ? pendingPatterns[editingIndex] ?? null : null
        }
        patternIndex={editingIndex}
        onClose={() => setEditingIndex(null)}
        onSave={handleEditSave}
        isProcessing={isInteractionBusy}
        enableErrorEditInUi={enableErrorEditInUi}
      />

      <WorkPatternImportCommitConfirmModal
        isOpen={!isBlocked && commitConfirmOpen}
        importableCount={commitConfirmCounts.importableCount}
        skippedCount={commitConfirmCounts.skippedCount}
        warningCount={commitConfirmCounts.warningCount}
        overrideWarnings={
          commitConfirmMode === "overrideWarnings" ||
          commitConfirmMode === "partialOverride"
        }
        onCancel={() => setCommitConfirmOpen(false)}
        onImport={() => {
          setCommitConfirmOpen(false);
          void commit({
            overrideUnacknowledgedWarnings:
              commitConfirmMode === "overrideWarnings" ||
              commitConfirmMode === "partialOverride",
          });
        }}
      />

      {redesignEnabled && (
        <WorkPatternImportReplaceFileConfirmModal
          isOpen={!isBlocked && pendingReplacementFile !== null}
          fileName={pendingReplacementFile?.name ?? ""}
          patternCount={pendingPatterns.length}
          onKeepCurrent={() => setPendingReplacementFile(null)}
          onReplace={() => void handleConfirmFileReplacement()}
        />
      )}

      <WorkPatternImportCancelConfirmModal
        isOpen={!isBlocked && cancelConfirmOpen}
        onKeepImporting={() => setCancelConfirmOpen(false)}
        onCancelAnyway={handleConfirmCancelImport}
      />

      <WorkPatternImportLeaveConfirmModal
        isOpen={isBlocked}
        onStay={stay}
        onLeave={leaveAnyway}
      />

      {/* When the clustered report is active the cycle grid is shown inline in
          each row's accordion — suppress the standalone Cycle preview section. */}
      {!useClusteredReport && succeededPatterns.length > 0 && (
        <section className={styles.gridSection}>
          <h3 className={styles.gridHeading}>Cycle preview</h3>
          {succeededPatterns.map((pattern, index) => (
            <WorkPatternCycleGrid
              key={`${pattern.workPatternName}-${index}`}
              pattern={pattern}
            />
          ))}
        </section>
      )}
    </div>
  );
}
